// Calculation functions for all calculators

export const formatCurrency = (num: number) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(num);
};

// FD Calculation
export const calculateFD = (
  amount: number,
  rate: number,
  tenure: number,
  compounding: "quarterly" | "monthly" | "yearly"
) => {
  const n =
    compounding === "monthly" ? 12 : compounding === "quarterly" ? 4 : 1;
  const t = tenure / 12;
  const maturity = amount * Math.pow(1 + rate / 100 / n, n * t);
  const interest = maturity - amount;
  return { maturity: Math.round(maturity), interest: Math.round(interest) };
};

// SIP Calculation
export const calculateSIP = (amount: number, rate: number, years: number) => {
  const monthlyRate = Math.pow(1 + rate / 100, 1 / 12) - 1;
  const months = years * 12;
  const futureValue =
    amount *
    ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) *
    (1 + monthlyRate);
  const invested = amount * months;
  const returns = futureValue - invested;
  return {
    futureValue: Math.round(futureValue),
    invested: Math.round(invested),
    returns: Math.round(returns),
  };
};

// Lumpsum Calculation
export const calculateLumpsum = (
  amount: number,
  rate: number,
  years: number
) => {
  const futureValue = amount * Math.pow(1 + rate / 100, years);
  const returns = futureValue - amount;
  return {
    futureValue: Math.round(futureValue),
    returns: Math.round(returns),
  };
};

// PPF Calculation
export const calculatePPF = (
  amount: number,
  years: number,
  rate: number = 7.1
) => {
  let maturity = 0;
  const r = rate / 100;
  for (let i = 0; i < years; i++) {
    maturity = (maturity + amount) * (1 + r);
  }
  const invested = amount * years;
  return {
    maturity: Math.round(maturity),
    interest: Math.round(maturity - invested),
    invested,
  };
};

// EMI Calculation
export const calculateEMI = (
  loanAmount: number,
  rate: number,
  tenure: number
) => {
  const r = rate / 100 / 12;
  const n = tenure * 12;
  const emi = (loanAmount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  const totalPayment = emi * n;
  const totalInterest = totalPayment - loanAmount;
  return {
    emi: Math.round(emi),
    totalPayment: Math.round(totalPayment),
    totalInterest: Math.round(totalInterest),
  };
};

// Income Tax Calculation (FY 2024-25)
export const calculateTax = (annualIncome: number, deductions: number) => {
  const calculateNewRegime = (income: number) => {
    let tax = 0;
    const taxableIncome = Math.max(0, income - 75000);
    if (taxableIncome <= 700000) {
      return 0;
    }

    if (taxableIncome > 300000) {
      tax += Math.min(taxableIncome - 300000, 300000) * 0.05;
    }
    if (taxableIncome > 600000) {
      tax += Math.min(taxableIncome - 600000, 300000) * 0.1;
    }
    if (taxableIncome > 900000) {
      tax += Math.min(taxableIncome - 900000, 300000) * 0.15;
    }
    if (taxableIncome > 1200000) {
      tax += Math.min(taxableIncome - 1200000, 300000) * 0.2;
    }
    if (taxableIncome > 1500000) {
      tax += (taxableIncome - 1500000) * 0.3;
    }

    return tax * 1.04;
  };

  const calculateOldRegime = (income: number) => {
    const taxableIncome = Math.max(0, income - deductions - 50000);
    if (taxableIncome <= 500000) {
      return 0;
    }

    let tax = 0;
    if (taxableIncome > 250000) {
      tax += Math.min(taxableIncome - 250000, 250000) * 0.05;
    }
    if (taxableIncome > 500000) {
      tax += Math.min(taxableIncome - 500000, 500000) * 0.2;
    }
    if (taxableIncome > 1000000) {
      tax += (taxableIncome - 1000000) * 0.3;
    }

    return tax * 1.04;
  };

  return {
    new: Math.round(calculateNewRegime(annualIncome)),
    old: Math.round(calculateOldRegime(annualIncome)),
  };
};

// NPS Calculation
export const calculateNPS = (
  monthly: number,
  age: number,
  returnRate: number,
  retirementAge: number = 60
) => {
  const years = retirementAge - age;
  const months = years * 12;
  const r = returnRate / 100 / 12;
  const totalCorpus = monthly * ((Math.pow(1 + r, months) - 1) / r);
  const invested = monthly * months;
  return {
    maturity: Math.round(totalCorpus),
    interest: Math.round(totalCorpus - invested),
    invested,
  };
};

// SSY Calculation
export const calculateSSY = (amount: number, rate: number = 8.2) => {
  const depositYears = 15;
  const maturityYears = 21;
  let maturity = 0;
  const r = rate / 100;

  for (let year = 1; year <= maturityYears; year++) {
    if (year <= depositYears) {
      maturity = (maturity + amount) * (1 + r);
    } else {
      maturity = maturity * (1 + r);
    }
  }
  const invested = amount * depositYears;
  return {
    maturity: Math.round(maturity),
    interest: Math.round(maturity - invested),
    invested,
  };
};

// RD Calculation
export const calculateRD = (amount: number, rate: number, years: number) => {
  const months = years * 12;
  let maturity = 0;
  for (let i = 0; i < months; i++) {
    const t_years = (months - i) / 12;
    maturity += amount * Math.pow(1 + rate / 100 / 4, 4 * t_years);
  }
  const invested = amount * months;
  return {
    maturity: Math.round(maturity),
    interest: Math.round(maturity - invested),
    invested,
  };
};

// Inflation Calculation
export const calculateInflation = (
  current: number,
  rate: number,
  years: number
) => {
  const futureValue = current * Math.pow(1 + rate / 100, years);
  return {
    futureValue: Math.round(futureValue),
    difference: Math.round(futureValue - current),
  };
};

// GST Calculation
export const calculateGST = (
  amount: number,
  rate: number,
  type: "exclusive" | "inclusive"
) => {
  let gst = 0;
  let total = 0;
  let net = 0;

  if (type === "exclusive") {
    gst = (amount * rate) / 100;
    total = amount + gst;
    net = amount;
  } else {
    total = amount;
    net = amount / (1 + rate / 100);
    gst = total - net;
  }
  return {
    gst: Math.round(gst),
    total: Math.round(total),
    net: Math.round(net),
  };
};

// CAGR Calculation
export const calculateCAGR = (start: number, end: number, years: number) => {
  const cagr = (Math.pow(end / start, 1 / years) - 1) * 100;
  return isNaN(cagr) ? "0.00" : cagr.toFixed(2);
};

// SWP Calculation
export const calculateSWP = (
  investment: number,
  withdrawal: number,
  rate: number,
  years: number
) => {
  let currentBalance = investment;
  const monthlyRate = rate / 100 / 12;
  const totalMonths = years * 12;
  let totalWithdrawn = 0;

  for (let i = 0; i < totalMonths; i++) {
    if (currentBalance >= withdrawal) {
      currentBalance -= withdrawal;
      totalWithdrawn += withdrawal;
    } else {
      totalWithdrawn += currentBalance;
      currentBalance = 0;
      break;
    }
    const interest = currentBalance * monthlyRate;
    currentBalance += interest;
  }

  return {
    finalValue: Math.round(currentBalance),
    totalWithdrawal: Math.round(totalWithdrawn),
    invested: investment,
    returns: Math.round(currentBalance + totalWithdrawn - investment),
  };
};

// EPF Calculation
export const calculateEPF = (
  basic: number,
  age: number,
  rate: number,
  increase: number,
  retirementAge: number = 58
) => {
  const years = Math.max(0, retirementAge - age);
  const months = years * 12;
  const r = rate / 100 / 12;

  let balance = 0;
  let currentBasic = basic;
  let totalEmployee = 0;
  let totalEmployer = 0;

  for (let m = 0; m < months; m++) {
    if (m > 0 && m % 12 === 0) {
      currentBasic = currentBasic * (1 + increase / 100);
    }

    const empCont = currentBasic * 0.12;
    let eps = 0;
    if (currentBasic > 15000) {
      eps = 1250;
    } else {
      eps = currentBasic * 0.0833;
    }

    const employerEPF = currentBasic * 0.12 - eps;

    balance = (balance + empCont + employerEPF) * (1 + r);
    totalEmployee += empCont;
    totalEmployer += employerEPF;
  }

  return {
    maturity: Math.round(balance),
    employee: Math.round(totalEmployee),
    employer: Math.round(totalEmployer),
    interest: Math.round(balance - totalEmployee - totalEmployer),
  };
};

// HRA Calculation
export const calculateHRA = (
  basic: number,
  da: number,
  received: number,
  rent: number,
  metro: boolean
) => {
  const salary = basic + da;
  const c1 = received;
  const c2 = metro ? salary * 0.5 : salary * 0.4;
  const c3 = Math.max(0, rent - salary * 0.1);
  const exemption = Math.min(c1, c2, c3);
  const taxable = Math.max(0, received - exemption);

  return {
    exemption: Math.round(exemption),
    taxable: Math.round(taxable),
  };
};

// Retirement Calculation
export const calculateRetirement = (
  currentAge: number,
  retireAge: number,
  expectancy: number,
  expenses: number,
  inflation: number,
  preRate: number,
  postRate: number
) => {
  const yearsToRetire = Math.max(0, retireAge - currentAge);
  const yearsInRetirement = Math.max(0, expectancy - retireAge);

  const monthlyExpensesAtRetire =
    expenses * Math.pow(1 + inflation / 100, yearsToRetire);
  const realReturn = (1 + postRate / 100) / (1 + inflation / 100) - 1;

  const months = yearsInRetirement * 12;
  const monthlyRealReturn = Math.pow(1 + realReturn, 1 / 12) - 1;

  const requiredCorpus =
    monthlyExpensesAtRetire *
    ((1 - Math.pow(1 + monthlyRealReturn, -months)) / monthlyRealReturn);

  const sipRateMonthly = preRate / 100 / 12;
  const accumulateMonths = yearsToRetire * 12;
  const sipRequired =
    requiredCorpus *
    (sipRateMonthly / (Math.pow(1 + sipRateMonthly, accumulateMonths) - 1));

  return {
    corpus: Math.round(requiredCorpus),
    monthlySIP: Math.round(sipRequired),
    expenseAtRetire: Math.round(monthlyExpensesAtRetire),
  };
};
